import React from 'react';
import {
    SUPPORT_HOW_TO,
    SUPPORT_NAV,
    SUPPORT_PAGE,
    SUPPORT_SECTIONS,
} from './supportGuideCopy';
import './SupportGuide.css';

function LoginPreview() {
    return (
        <div className="sg-mock sg-mock--login" aria-hidden>
            <p className="sg-mock__brand">Oola Lifestyle Framework</p>
            <div className="sg-mock__card">
                <p className="sg-mock__kicker">Welcome back</p>
                <p className="sg-mock__card-title">Access your programs</p>
                <div className="sg-mock__tabs">
                    <span>Phone</span>
                    <span className="is-on">Customer Id</span>
                </div>
                <span className="sg-mock__field">Customer Id</span>
                <span className="sg-mock__field">Password</span>
                <span className="sg-mock__btn">Sign in</span>
            </div>
        </div>
    );
}

function StepsPreview() {
    const steps = ['1  Assess', '2  Goals', '3  Actions'];
    return (
        <div className="sg-mock" aria-hidden>
            <p className="sg-mock__kicker">Design My Life</p>
            <div className="sg-mock__pills">
                {steps.map((label, index) => (
                    <span
                        key={label}
                        className={index === 0 ? 'is-on' : undefined}
                    >
                        {label}
                    </span>
                ))}
            </div>
            <p className="sg-mock__card-title">Life assessment</p>
            <p className="sg-mock__note">
                Seven areas, then the wheel. Goals and daily actions come next.
                Breakthrough stays locked until all three are saved.
            </p>
        </div>
    );
}

function NavPreview() {
    const items = [
        'Dashboard',
        'Action Tracker',
        'Goals',
        'E-Learning',
        'Additional Programs',
        'Profile',
    ];
    return (
        <div className="sg-mock sg-mock--nav" aria-hidden>
            <p className="sg-mock__brand">Oola</p>
            <ul>
                {items.map((label, index) => (
                    <li key={label} className={index === 0 ? 'is-on' : undefined}>
                        {label}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function StreakPreview() {
    return (
        <div className="sg-mock" aria-hidden>
            <div className="sg-mock__tabs">
                <span className="is-on">Tracker</span>
                <span>Daily Actions</span>
                <span>To-Do List</span>
            </div>
            <div className="sg-mock__ring">
                <strong>3</strong>
                <span>actions to count today</span>
            </div>
            <p className="sg-mock__note">
                Fewer than 3 on a calendar day breaks the current streak. The
                longest streak stays.
            </p>
        </div>
    );
}

function GoalsPreview() {
    return (
        <div className="sg-mock" aria-hidden>
            <p className="sg-mock__kicker">From the blueprint</p>
            <ul className="sg-mock__list">
                <li>Top 7 goals</li>
                <li className="is-on">Oola One — the priority for the year</li>
                <li>Habits are checked in Action Tracker</li>
            </ul>
        </div>
    );
}

function LearnPreview() {
    return (
        <div className="sg-mock" aria-hidden>
            <p className="sg-mock__kicker">E-Learning</p>
            <ul className="sg-mock__list">
                <li className="is-on">My Breakthrough — 12 weeks, every 7 days</li>
                <li>Watch, then Read, then Do</li>
                <li>My Path — after Breakthrough, about every 30 days</li>
            </ul>
        </div>
    );
}

function ProgramsPreview() {
    return (
        <div className="sg-mock" aria-hidden>
            <p className="sg-mock__kicker">Not part of the Framework</p>
            <p className="sg-mock__card-title">Additional Programs</p>
            <p className="sg-mock__note">
                Only courses purchased on this Customer Id. An empty list means
                the order is on another id, or there is no add-on purchase.
            </p>
        </div>
    );
}

function ProfilePreview() {
    return (
        <div className="sg-mock" aria-hidden>
            <p className="sg-mock__kicker">Profile</p>
            <span className="sg-mock__field">Time zone — this sets “today”</span>
            <p className="sg-mock__note">
                Changing the phone clock does not move the streak. The saved
                zone does.
            </p>
        </div>
    );
}

const PREVIEWS = {
    login: LoginPreview,
    steps: StepsPreview,
    nav: NavPreview,
    streak: StreakPreview,
    goals: GoalsPreview,
    learn: LearnPreview,
    programs: ProgramsPreview,
    profile: ProfilePreview,
};

function Section({ section }) {
    const Preview = section.preview ? PREVIEWS[section.preview] : null;

    return (
        <section id={section.id} className="sg-section">
            <div className="sg-section__copy">
                {section.phase ? (
                    <p className="sg-section__phase">{section.phase}</p>
                ) : null}
                <h2>{section.title}</h2>
                <p className="sg-section__summary">{section.summary}</p>
                <h3>Walk them through</h3>
                <ol>
                    {section.walk.map((step) => (
                        <li key={step}>{step}</li>
                    ))}
                </ol>
                <h3>If they say…</h3>
                <ul className="sg-cases">
                    {section.cases.map((item) => (
                        <li key={item.issue}>
                            <strong>{item.issue}</strong>
                            <span>{item.do}</span>
                        </li>
                    ))}
                </ul>
            </div>
            {Preview ? (
                <div className="sg-section__preview">
                    <p className="sg-section__preview-label">What they see</p>
                    <Preview />
                </div>
            ) : null}
        </section>
    );
}

const SupportGuidePage = () => (
    <div className="sg-page">
        <header className="sg-hero">
            <p className="sg-hero__eyebrow">{SUPPORT_PAGE.eyebrow}</p>
            <h1>{SUPPORT_PAGE.title}</h1>
            <p className="sg-hero__subtitle">{SUPPORT_PAGE.subtitle}</p>
            <div className="sg-hero__notes">
                {SUPPORT_PAGE.notes.map((note) => (
                    <p key={note}>{note}</p>
                ))}
            </div>
        </header>

        <section className="sg-howto">
            <h2>{SUPPORT_HOW_TO.title}</h2>
            <ol>
                {SUPPORT_HOW_TO.items.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ol>
        </section>

        <nav className="sg-nav" aria-label="Guide sections">
            {SUPPORT_NAV.map((item, index) => (
                <a key={item.id} href={`/support-guide#${item.id}`}>
                    <span>{index + 1}</span>
                    {item.label}
                </a>
            ))}
        </nav>

        {SUPPORT_SECTIONS.map((section) => (
            <Section key={section.id} section={section} />
        ))}

        <footer className="sg-footer">
            <p>
                Member login:{' '}
                <a href="https://dashboard.oolalife.com/login">
                    dashboard.oolalife.com/login
                </a>
                . This guide:{' '}
                <a href="https://dashboard.oolalife.com/support-guide">
                    dashboard.oolalife.com/support-guide
                </a>
                .
            </p>
        </footer>
    </div>
);

export default SupportGuidePage;
